/**
 * Professional Dictation Panel
 *
 * Provides a prominent, interactive voice dictation experience
 * with visual feedback, waveform animation, and clear status indicators.
 *
 * @module presentation/components
 */

import { useState, useEffect, useRef } from 'react'
import {
  Mic,
  MicOff,
  Square,
  Pause,
  Loader2,
  AlertCircle,
  HelpCircle,
  Clock,
  Volume2,
  CheckCircle2,
} from 'lucide-react'
import { Button } from './ui/Button'
import { useDictation, useDictationAvailable } from '@/application/dictation'
import { ControlesDeDictado } from './dictation/ControlesDeDictado'

// =============================================================================
// Types
// =============================================================================

export interface DictationPanelProps {
  /** Callback when text is transcribed */
  /**
   * Recibe el texto transcrito y LA SECCION A LA QUE PERTENECE.
   *
   * La seccion se decide cuando empieza el dictado, no cuando llega el
   * texto. Al detener, el motor entrega todavia el ultimo trozo de audio ya
   * grabado, y para entonces el cursor puede estar en otro campo: sin este
   * dato, esa frase aterriza donde no se dijo.
   */
  onTranscript: (text: string, seccion?: string) => void
  /** Active section being dictated (null if no section selected) */
  activeSection?: 'findings' | 'impression' | 'recommendations' | 'technique' | null
  /** Section labels for display */
  sectionLabels?: Record<string, string>
  /** Whether panel is disabled */
  disabled?: boolean
  /** Compact mode for inline use */
  compact?: boolean
  /** Additional class names */
  className?: string
}

interface VoiceCommand {
  command: string
  result: string
}

// =============================================================================
// Constants
// =============================================================================

const VOICE_COMMANDS: VoiceCommand[] = [
  { command: 'punto', result: '.' },
  { command: 'coma', result: ',' },
  { command: 'párrafo', result: '¶' },
  { command: 'nueva línea', result: '↵' },
  { command: 'dos puntos', result: ':' },
  { command: 'guión', result: '-' },
]

const SECTION_LABELS: Record<string, string> = {
  technique: 'Técnica',
  findings: 'Hallazgos',
  impression: 'Impresión Diagnóstica',
  recommendations: 'Recomendaciones',
}

// =============================================================================
// Sub-components
// =============================================================================

function WaveformAnimation({ isActive }: { isActive: boolean }) {
  if (!isActive) return null

  return (
    <div className="flex items-center justify-center gap-1 h-6 my-2">
      {[...Array(7)].map((_, i) => (
        <div
          key={i}
          className="w-1 bg-red-500 rounded-full animate-waveform"
          style={{
            animationDelay: `${i * 0.08}s`,
          }}
        />
      ))}
    </div>
  )
}

function SessionTimer({ isRunning, seconds }: { isRunning: boolean; seconds: number }) {
  const formatTime = (secs: number) => {
    const mins = Math.floor(secs / 60)
    const remainingSecs = secs % 60
    return `${mins.toString().padStart(2, '0')}:${remainingSecs.toString().padStart(2, '0')}`
  }

  return (
    <div className={`flex items-center gap-1.5 text-sm ${isRunning ? 'text-red-600 dark:text-red-400' : 'text-[var(--foreground-muted)]'}`}>
      <Clock className="h-3.5 w-3.5" />
      <span className="font-mono font-medium">{formatTime(seconds)}</span>
    </div>
  )
}

function VoiceCommandsHelp() {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 text-xs text-[var(--foreground-muted)] hover:text-[var(--foreground)] transition-colors"
      >
        <HelpCircle className="h-3.5 w-3.5" />
        <span>Comandos de voz</span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute bottom-full left-0 mb-2 z-50 w-56 p-3 bg-[var(--card)] border border-[var(--border)] rounded-lg shadow-lg">
            <h4 className="font-medium text-sm mb-2 text-[var(--foreground)]">
              Comandos de Voz
            </h4>
            <p className="text-xs text-[var(--foreground-muted)] mb-2">
              Diga estas palabras para insertar puntuación:
            </p>
            <div className="grid grid-cols-2 gap-1">
              {VOICE_COMMANDS.map((cmd) => (
                <div
                  key={cmd.command}
                  className="flex items-center justify-between text-xs px-2 py-1 bg-[var(--background-subtle)] rounded"
                >
                  <span className="text-[var(--foreground-muted)]">
                    {cmd.command}
                  </span>
                  <span className="font-mono text-[var(--primary)] font-bold">
                    {cmd.result}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function DictationPanel({
  onTranscript,
  activeSection,
  sectionLabels = SECTION_LABELS,
  disabled = false,
  compact = false,
  className = '',
}: DictationPanelProps) {
  const isSupported = useDictationAvailable()
  const [seconds, setSeconds] = useState(0)
  const [lastTranscript, setLastTranscript] = useState<string | null>(null)

  // Seccion en la que se pulso "dictar". Es el destino del texto hasta que
  // se vuelva a pulsar, incluido el trozo que llega despues de detener.
  const seccionAlIniciar = useRef<string | null>(null)

  // Check if dictation is blocked because no section is selected
  const noSectionSelected = !activeSection

  const {
    isListening,
    grabando,
    transcribiendo,
    pausado,
    transcript,
    error,
    startListening,
    stopListening,
    pausar,
    cancelar,
  } = useDictation({
    lang: 'es-CO',
    processCommands: true,
    onResult: (text) => {
      onTranscript(text, seccionAlIniciar.current ?? activeSection ?? undefined)
      setLastTranscript(text)
      // Clear last transcript after 3 seconds
      setTimeout(() => setLastTranscript(null), 3000)
    },
  })

  // El cronometro mide microfono abierto, no sesion abierta.
  useEffect(() => {
    if (!grabando) return

    const interval = setInterval(() => setSeconds((s) => s + 1), 1000)
    return () => clearInterval(interval)
  }, [grabando])

  const handleToggle = () => {
    if (noSectionSelected) return

    if (isListening) {
      // No se limpia `seccionAlIniciar`: el ultimo trozo llega despues y
      // pertenece a lo que se acaba de dictar.
      stopListening()
    } else {
      seccionAlIniciar.current = activeSection ?? null
      setSeconds(0)
      startListening()
    }
  }

  // Not supported message
  if (!isSupported) {
    return (
      <div className={`${compact ? 'p-3' : 'p-6'} bg-[var(--background-subtle)] rounded-xl border border-[var(--border)] ${className}`}>
        <div className="text-center">
          <MicOff className={`${compact ? 'h-6 w-6' : 'h-12 w-12'} mx-auto text-[var(--foreground-muted)] mb-2`} />
          {!compact && (
            <>
              <h3 className="font-medium text-[var(--foreground)] mb-1">
                Dictado no disponible
              </h3>
              <p className="text-sm text-[var(--foreground-muted)]">
                Su navegador no soporta reconocimiento de voz.
                <br />
                Use Chrome, Edge o Safari para esta función.
              </p>
            </>
          )}
        </div>
      </div>
    )
  }

  // Compact mode for inline use (e.g., viewer panel)
  if (compact) {
    return (
      <div data-dictation-panel className={`flex items-center gap-3 ${className}`}>
        <button
          type="button"
          onClick={handleToggle}
          disabled={disabled || noSectionSelected}
          className={`
            relative w-12 h-12 rounded-full flex items-center justify-center
            transition-all duration-200 focus:outline-none focus:ring-2
            ${(disabled || noSectionSelected) ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
            ${grabando
              ? 'bg-red-500 text-white focus:ring-red-300 scale-105'
              : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-300'}
          `}
        >
          <Mic className={`h-5 w-5 ${grabando ? 'animate-pulse' : ''}`} />
          {grabando && (
            <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-30" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          {grabando ? (
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
              </span>
              <span className="text-xs text-red-400 font-medium">Grabando</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                {Math.floor(seconds / 60).toString().padStart(2, '0')}:{(seconds % 60).toString().padStart(2, '0')}
              </span>
            </div>
          ) : transcribiendo ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin text-sky-400" />
              <span className="text-xs text-sky-400 font-medium">
                Micrófono cerrado · transcribiendo
              </span>
            </div>
          ) : pausado ? (
            <span className="text-xs text-amber-400">En pausa · el micrófono está cerrado</span>
          ) : noSectionSelected ? (
            <span className="text-xs text-amber-400">Seleccione un campo primero</span>
          ) : (
            <span className="text-xs text-gray-400">Clic para dictar en {sectionLabels[activeSection!]}</span>
          )}
          {transcript && (
            <p className="text-xs text-gray-300 truncate mt-0.5">{transcript}</p>
          )}
        </div>
        <ControlesDeDictado
          grabando={grabando}
          transcribiendo={transcribiendo}
          pausado={pausado}
          onPausar={pausar}
          onReanudar={startListening}
          onDetener={stopListening}
          onCancelar={cancelar}
        />
      </div>
    )
  }

  return (
    <div data-dictation-panel className={`bg-[var(--background-subtle)] rounded-xl border border-[var(--border)] overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--border)] bg-[var(--card)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Volume2 className="h-4 w-4 text-[var(--primary)]" />
            <span className="font-medium text-sm text-[var(--foreground)]">
              Dictado por Voz
            </span>
          </div>
          <SessionTimer isRunning={grabando} seconds={seconds} />
        </div>
        {activeSection ? (
          <div className="mt-1 text-xs text-[var(--foreground-muted)]">
            Dictando en: <span className="font-medium text-[var(--primary)]">{sectionLabels[activeSection]}</span>
          </div>
        ) : (
          <div className="mt-1 text-xs text-amber-600 dark:text-amber-400">
            Haga clic en un campo para activar el dictado
          </div>
        )}
      </div>

      {/* Main Control Area */}
      <div className="p-6">
        {/* Status Badge */}
        <div className="text-center mb-4">
          {grabando ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full border-2 border-red-300">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
              </span>
              <span className="text-sm font-semibold">GRABANDO</span>
            </div>
          ) : transcribiendo ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 rounded-full border-2 border-sky-300">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm font-semibold">TRANSCRIBIENDO</span>
            </div>
          ) : pausado ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 alert-warning rounded-full">
              <Pause className="h-4 w-4" />
              <span className="text-sm font-medium">En pausa</span>
            </div>
          ) : error ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 alert-error rounded-full">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Error</span>
            </div>
          ) : noSectionSelected ? (
            <div className="inline-flex items-center gap-2 px-4 py-2 alert-warning rounded-full">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-medium">Seleccione un campo</span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-4 py-2 alert-success rounded-full">
              <Mic className="h-4 w-4" />
              <span className="text-sm font-medium">Listo para dictar</span>
            </div>
          )}
        </div>

        {/* Waveform */}
        <WaveformAnimation isActive={grabando} />

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 alert-error rounded-lg text-sm text-center">
            {error}
          </div>
        )}

        {/* Main Microphone Button */}
        <div className="flex justify-center mb-4">
          <button
            type="button"
            onClick={handleToggle}
            disabled={disabled || noSectionSelected}
            // Un boton que solo lleva icono no tiene nombre: ni lo anuncia un
            // lector de pantalla ni lo encuentra una prueba por su rol. El
            // microfono del visor ya lo declaraba; este no.
            aria-label={isListening ? 'Detener el dictado' : 'Dictar en este campo'}
            title={isListening ? 'Detener el dictado' : 'Dictar en este campo'}
            className={`
              relative w-24 h-24 rounded-full flex items-center justify-center
              transition-all duration-300 focus:outline-none focus:ring-4
              ${(disabled || noSectionSelected) ? 'opacity-40 cursor-not-allowed grayscale' : 'cursor-pointer'}
              ${isListening
                ? 'bg-red-500 hover:bg-red-600 text-white focus:ring-red-200 shadow-xl shadow-red-500/40 scale-110'
                : noSectionSelected
                  ? 'bg-gray-400 text-white'
                  : 'bg-[var(--primary)] hover:bg-[var(--primary)]/90 text-white focus:ring-[var(--primary)]/30 shadow-lg hover:shadow-xl hover:scale-105'}
            `}
          >
            <Mic className={`h-10 w-10 ${isListening ? 'animate-pulse' : ''}`} />

            {/* Ripple effect when listening */}
            {isListening && (
              <>
                <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-20" />
                <span className="absolute inset-[-12px] rounded-full border-4 border-red-300/50 animate-pulse" />
              </>
            )}
          </button>
        </div>

        {/* Stop Button */}
        {isListening && (
          <div className="flex justify-center mb-4">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={stopListening}
              className="text-red-600 dark:text-red-400 hover:text-red-700 dark:text-red-300 hover:bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800"
            >
              <Square className="h-4 w-4 mr-1.5 fill-current" />
              Detener Grabación
            </Button>
          </div>
        )}

        {/* Instructions */}
        {!isListening && !error && (
          <p className="text-center text-sm text-[var(--foreground-muted)]">
            {noSectionSelected
              ? 'Haga clic en Técnica, Hallazgos, Impresión o Recomendaciones para activar'
              : 'Presione el botón y comience a hablar'}
          </p>
        )}

        {/* Interim Transcript Preview */}
        {isListening && transcript && (
          <div className="mt-4 p-3 alert-warning rounded-lg">
            <p className="text-xs mb-1 font-medium">Escuchando...</p>
            <p className="text-sm italic">
              {transcript}
            </p>
          </div>
        )}

        {/* Last Transcript Success */}
        {lastTranscript && !transcript && (
          <div className="mt-4 p-3 alert-success rounded-lg">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs mb-1 font-medium">Texto agregado</p>
                <p className="text-sm">
                  {lastTranscript.slice(0, 100)}{lastTranscript.length > 100 ? '...' : ''}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer with Commands Help */}
      <div className="px-4 py-2 border-t border-[var(--border)] bg-[var(--card)]">
        <VoiceCommandsHelp />
      </div>

      {/* Waveform animation styles */}
      <style>{`
        @keyframes waveform {
          0%, 100% { height: 6px; }
          50% { height: 20px; }
        }
        .animate-waveform {
          animation: waveform 0.6s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}

/**
 * Compact dictation indicator for inline use
 */
export function DictationIndicator({
  isListening,
  transcript,
}: {
  isListening: boolean
  transcript?: string
}) {
  if (!isListening) return null

  return (
    <div className="flex items-center gap-2 px-2 py-1 bg-red-100 dark:bg-red-900/40 rounded-md border border-red-200 dark:border-red-800">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600"></span>
      </span>
      <span className="text-xs text-red-700 dark:text-red-300 font-medium">
        {transcript ? transcript.slice(0, 40) + (transcript.length > 40 ? '...' : '') : 'Escuchando...'}
      </span>
    </div>
  )
}
